import { CurrencyType, IBaseBlockShowResource, Id } from "@dgs/core";
import { HotelStayStatus } from "../../../../admin/src/shared/types/hotelStay";
import { GuestSelfTravelResource } from "../../../../shared/types/selfTravel";
import { TravelInquiryCreateRequest } from "../../../../shared/types/travelInquiry";
import { CompanionResource, DataFieldValue, GuestResource } from "./guest";
import { GuestTypeOptionResource } from "./guestType";
import { HotelMainStayResource } from "./hotel";
import { PaymentProviderOptionResource } from "./paymentProviderOptionResource";
import { RuleSetResource } from "./ruleSet";
import { RegistrationFormStaticStepResource, RegistrationFormStepResource } from "./step";
import { AssignableTravelSegmentType, TravelSegmentCreateRequest } from "./travelSegment";

export enum RegistrationStatus {
	REGISTERED = "STATUS1",
	REGISTERED_WITH_INVOICE = "STATUS2",
	REGISTERED_WITH_PAYMENT_PROVIDER = "STATUS3",
	REGISTERED_WITH_POSITIVE_SCREENING = "STATUS4",
	REGISTRATION_CHANGED = "STATUS5",
	WAITING_LIST = "STATUS6",
	WAITING_LIST_WITH_POSITIVE_SCREENING = "STATUS7",
	REGISTRATION_CHANGED_WITH_POSITIVE_SCREENING = "STATUS8",
}

interface PaymentRegistrationResponse {
	paymentLink: string;
	status: RegistrationStatus.REGISTERED_WITH_PAYMENT_PROVIDER;
}

interface OtherRegistrationResponse {
	paymentLink: null;
	status: Exclude<RegistrationStatus, RegistrationStatus.REGISTERED_WITH_PAYMENT_PROVIDER>;
}

export type RegistrationResponse = PaymentRegistrationResponse | OtherRegistrationResponse;

export enum PaymentTransactionItemType {
	FULL_PAYMENT = "FULL_PAYMENT",
	FINAL_PAYMENT = "FINAL_PAYMENT",
	DEPOSIT_PAYMENT = "DEPOSIT_PAYMENT",
}

export interface PaymentTransactionItem {
	id: string | null;
	type: PaymentTransactionItemType;
	shopItemId: string;
	taxRate: number;
	netPrice: number;
	grossPrice: number;
	totalPrice: number;
	refundTotalPrice: number | null;
	refundNetPrice: number | null;
	depositAmountInPercent: number | null;
	name: string | null;
	description: string | null;
	discountInPercent: number | null;
	isReverseCharge: boolean;
	hasCancellationConfig: boolean;
	currencyType: CurrencyType;
	dueDate?: string;
	paidAt: string | null;
	paymentTransactionId: Id | null;
	refundTransactionId: Id | null;
}

export interface ShopSummaryTaxResource {
	percent: number;
	amount: number;
}

export interface ShopSummaryTaxWithDisclaimerIndex {
	percent: number;
	amount: number;
	index: number;
}

export interface RegistrationShopSummaryResource {
	transactionItemsToBeRefunded: PaymentTransactionItem[];
	transactionItemsToBePaidNow: PaymentTransactionItem[];
	transactionItemsToBePaidLater: PaymentTransactionItem[];
	transactionItemsAlreadyProcessed: PaymentTransactionItem[];
	shopSummaryHash: string;
	totalAmount: number;
	alreadyPaidTotalAmount: number;
	payLaterTotalAmount: number;
	payNowTotalAmount: number;
	toBeRefundedAmount: number;
	toBePaidTaxes: ShopSummaryTaxResource[];
	totalTaxes: ShopSummaryTaxResource[];
	paymentProviders: PaymentProviderOptionResource[];
}

export interface RegistrationSummaryNotAvailableResource {
	hasSummaryError: boolean;
}

export interface RegistrationFormPrivateShowResource {
	id: Id;
	privateBlock: IBaseBlockShowResource | null;
	type: "PRIVATE";
	isCodePageEnabled: boolean;
	codePageBlock: IBaseBlockShowResource | null;
}

export interface RegistrationFormPaymentOutstandingResource {
	id: Id;
	paymentLink: string;
	type: "PAYMENT_OUTSTANDING";
}

export interface RegistrationFormClosedShowResource {
	id: Id;
	closedBlock: IBaseBlockShowResource | null;
	type: "CLOSED";
}

export interface TravelSectionSettingsResource {
	availableTravelSegmentTypes: AssignableTravelSegmentType[];
	minTravels: number | null;
	maxTravels: number | null;
	canCreateTravelSegments: boolean;
	canSelectTravelTemplates: boolean;
	canSelectTravelSegments: boolean;
	canCreateTravelInquiries: boolean;
	canSelfTravel: boolean;
	isRemarksFieldRequired: boolean;
}

export interface RegistrationFormResource {
	type: "FORM";
	id: Id;
	steps: RegistrationFormStepResource[];
	ruleSets: RuleSetResource[];
	guest: GuestResource;
	isCancelable: boolean;
	isEditable: boolean;
	isDeclinable: boolean;
	isShopEnabled: boolean;
	isReplacementEnabled: boolean;
	isEmailGateEnabled: boolean;

	landingBlockId: Id | null;
	summaryBlockId: Id | null;
	emailGateBlockId: Id | null;

	closedBlockId: number | null;
	successBlockId: number | null;
	editedBlockId: number | null;
	declinedBlockId: number | null;
	cancelledBlockId: number | null;
	replacedBlockId: number | null;
	waitingListConfirmationBlockId: number | null;
	paymentSuccessfulBlockId: number | null;
	paymentLinkBlockId: number | null;
	alreadyPaidBlockId: number | null;
	emailGateWelcomeBlockId: number | null;
	emailGateWelcomeBackBlockId: number | null;
	confirmedBlockId: number | null;

	visibility: RegistrationFormVisibility;

	travelSectionSettings: TravelSectionSettingsResource | null;
	defaultGuestType: GuestTypeOptionResource | null;
}

export interface IConfirmResource {
	step: RegistrationFormStaticStepResource;
}

export interface IConfirmReplacementResource {
	step: RegistrationFormStaticStepResource | null;
}

export type IRegistrationFormShowResource =
	| RegistrationFormPrivateShowResource
	| RegistrationFormResource
	| RegistrationFormClosedShowResource
	| RegistrationFormPaymentOutstandingResource;

export enum RegistrationFormVisibility {
	ALL = "all",
	PRIVATE = "private",
	PUBLIC = "public",
	CLOSED = "closed",
}

export enum RegistrationFormEmailGateResult {
	WELCOME_BACK = "STATUS1",
	INVITED = "STATUS2",
	CONTINUE = "STATUS3",
}

interface RegistrationFormEmailGateWelcomeBack {
	result: RegistrationFormEmailGateResult.WELCOME_BACK;
}

interface RegistrationFormEmailGateInvited {
	result: RegistrationFormEmailGateResult.INVITED;
}

interface RegistrationFormEmailGateContinue {
	result: RegistrationFormEmailGateResult.CONTINUE;
	token: string;
}

export type RegistrationFormEmailGateResource =
	| RegistrationFormEmailGateWelcomeBack
	| RegistrationFormEmailGateInvited
	| RegistrationFormEmailGateContinue;

export interface RegistrationFormConsentBody {
	id: Id;
	value: boolean;
}

export interface RegistrationFormEmailGateRequest {
	email: string;
	consents: RegistrationFormConsentBody[];
	regionId: Id | null;
}

export const isRegistrationFormClosed = (
	registrationForm: IRegistrationFormShowResource,
): registrationForm is RegistrationFormClosedShowResource => {
	return registrationForm.type === "CLOSED";
};

export const isRegistrationFormPrivate = (
	registrationForm: IRegistrationFormShowResource,
): registrationForm is RegistrationFormPrivateShowResource => {
	return registrationForm.type === "PRIVATE";
};

export const isRegistrationFormPaymentOutstanding = (
	registrationForm: IRegistrationFormShowResource,
): registrationForm is RegistrationFormPaymentOutstandingResource => {
	return registrationForm.type === "PAYMENT_OUTSTANDING";
};

export interface RegistrationTravelDirectionRequest {
	travelSegmentIds: Id[];
	travelSegmentRequests: TravelSegmentCreateRequest[];
	travelInquiryRequest: TravelInquiryCreateRequest | null;
	selfTravelRequest: GuestSelfTravelResource | null;
	travelTemplateId: Id | null;
}

export interface RegistrationTravelRequest {
	id: Id | null;
	arrival: RegistrationTravelDirectionRequest;
	departure: RegistrationTravelDirectionRequest;
}

export interface HotelStayResource {
	id: Id | null;
	status: HotelStayStatus;
	hotelStayTypeId: number;
	checkIn: string;
	checkOut: string;
	requestedHotelId: number | null;
	requestedRoomTypeId: number | null;
	remarkByGuest: string | null;
	additionalPersonsText: string | null;
	additionalPersonsEnabled: boolean;
	mainStay: HotelMainStayResource | null;
}

export interface RegistrationHotelStayRequest {
	id: Id | null;
	hotelStayTypeId: number;
	checkIn: string;
	checkOut: string;
	requestedHotelId: number | null;
	requestedRoomTypeId: number | null;
	remarkByGuest: string | null;
	additionalPersonsText: string | null;
	additionalPersonsEnabled: boolean;
	cancellationRequested: boolean;
}

export interface SubmitRegistrationRequest {
	dataFieldValues: DataFieldValue[];
	consents: RegistrationFormConsentBody[];
	companions: CompanionResource[];
	travels: RegistrationTravelRequest[];
	shopSummaryHash: string | null;
	paymentProviderId: Id | null;
	token: string | undefined;
	registrationLocale: string;
	hotelStays: RegistrationHotelStayRequest[];
	regionId: Id | null;
}
